import { useState } from "react";
import { Icon } from "./Icon";
import { goToLookup } from "./goToLookup";
import { packagesFor } from "../../config/pricing.js";

const [SL_RISK, SL_COMPLETE] = packagesFor("servicelad");

const REPORT_PAGES = [
  {
    id: "overview",
    number: "01",
    tabTitle: "Overview & Health",
    tabBadge: "Pass + 1 alert",
    badgeType: "amber",
    bundle: "both",
  },
  {
    id: "damage",
    number: "02",
    tabTitle: "Write-off & Damage",
    tabBadge: "Cat S recorded",
    badgeType: "amber",
    bundle: "both",
  },
  {
    id: "service",
    number: "03",
    tabTitle: "Service History",
    tabBadge: "4 main dealer events",
    badgeType: "blue",
    bundle: "complete",
  },
  {
    id: "mileage",
    number: "04",
    tabTitle: "Mileage & Rollback",
    tabBadge: "Rollback clear",
    badgeType: "green",
    bundle: "both",
  },
  {
    id: "identity",
    number: "05",
    tabTitle: "Plates, V5C & Colour",
    tabBadge: "1 plate transfer",
    badgeType: "neutral",
    bundle: "both",
  },
];

const sampleDamageRecord = {
  // Mirrors CheckCar VehicleHistory.writeoff[] shape
  status: "CAT S STRUCTURAL",
  categoryLetter: "S",
  categoryName: "Category S",
  categoryLabel: "Category S: Structural damage / repairable",
  categoryTag: "Structural damage",
  lossDate: "4 July 2026",
  source: "MIAFTR",
  country: null,
  insurerName: "AGEAS INSURANCE LIMITED",
  damageLocations: ["Front", "Nearside"],
  damageZones: ["front", "nearside"],
  damageType: "Front and nearside impact",
  repairCostEstimate: null,
  postRepairMot: null,
  showUkBadge: true,
};

function isUkIncident(record) {
  if (!record) return false;
  if (record.showUkBadge) return true;
  const c = (record.country || "").trim().toUpperCase();
  const s = (record.source || "").trim().toUpperCase();
  return c === "UK" || c === "GB" || c === "UNITED KINGDOM" || s === "MIAFTR";
}

export default function SlReportShowcase() {
  const [activePage, setActivePage] = useState(0);
  const [selectedDamagePart, setSelectedDamagePart] = useState("front");

  const page = REPORT_PAGES[activePage];

  const goNext = () => setActivePage((prev) => (prev + 1) % REPORT_PAGES.length);
  const goPrev = () => setActivePage((prev) => (prev - 1 + REPORT_PAGES.length) % REPORT_PAGES.length);

  return (
    <section className="relative z-10 mx-auto max-w-6xl scroll-mt-24 px-4 py-12 sm:px-6" id="sample-report">
      {/* Section Heading */}
      <div className="mx-auto mb-10 max-w-2xl text-center">
        <p className="font-heading text-xs font-medium uppercase tracking-[0.22em] text-primary">Interactive Sample</p>
        <h2 className="mt-3 font-heading text-balance text-3xl font-semibold tracking-tight md:text-4xl">
          Look inside the ServiceLad report.
        </h2>
        <p className="mt-3 text-sm text-muted-foreground sm:text-base">
          Click the sections below to flip through real report pages: damage diagnostics, manufacturer service logs, mileage trail and logbook checks.
        </p>
      </div>

      {/* Main Showcase Card Container */}
      <div className="sl-showcase-wrap rounded-3xl border border-border bg-card p-4 shadow-[0_24px_50px_-28px_hsl(var(--primary)/0.2)] sm:p-6 md:p-8">
        <span className="sl-showcase-ear">SAMPLE REPORT</span>
        {/* Interactive Navigation Tabs */}
        <div className="sl-showcase-nav-bar mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-border/70 pb-4">
          <div className="sl-showcase-tabs flex flex-wrap items-center gap-1.5 sm:gap-2">
            {REPORT_PAGES.map((item, idx) => {
              const isActive = idx === activePage;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActivePage(idx)}
                  className={`sl-showcase-tab ${isActive ? "is-active" : ""}`}
                  aria-selected={isActive}
                  aria-label={item.tabTitle}
                  role="tab"
                >
                  <span className="sl-showcase-tab-num">{item.number}</span>
                  <span className="sl-showcase-tab-title">{item.tabTitle}</span>
                  {item.bundle === "complete" ? (
                    <span className="sl-showcase-tab-pill sl-pill-blue">Complete only</span>
                  ) : null}
                </button>
              );
            })}
          </div>

          <div className="sl-showcase-pager flex items-center gap-2 text-xs text-muted-foreground">
            <span className="sl-showcase-mobile-title">{page.tabTitle}</span>
            <span className="sl-showcase-page-count">
              <span className="font-medium text-foreground">Page {activePage + 1}</span> of {REPORT_PAGES.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={goPrev}
                className="sl-showcase-arrow-btn"
                aria-label="Previous report page"
              >
                <Icon name="chevron-left" size={16} />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="sl-showcase-arrow-btn"
                aria-label="Next report page"
              >
                <Icon name="chevron-right" size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Report Document Presentation */}
        <div className="sl-report-doc-surface rounded-2xl border border-border/80 bg-background p-4 sm:p-6 lg:p-7">
          {/* Doc Header Row */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-4 text-xs">
            <div className="flex items-center gap-3">
              <span className="font-heading font-bold tracking-wider text-primary">SERVICELAD</span>
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 font-medium text-primary">
                Digital Vehicle Report
              </span>
              <span className="hidden text-muted-foreground sm:inline">REF: SL-8842-GB</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Icon name="clock" size={13} className="text-primary" />
              <span>Prepared from official records, insurance databases, finance registers and manufacturer history</span>
            </div>
          </div>

          {/* PAGE 1: OVERVIEW & HEALTH CHECK */}
          {activePage === 0 ? (
            <div className="sl-report-page-anim space-y-6">
              {/* Vehicle Hero Strip */}
              <div className="relative overflow-visible rounded-2xl border border-border/70 bg-card p-5 sm:p-6">
                <div className="grid items-center gap-6 md:grid-cols-[1.3fr_1fr]">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="sl-sample-plate sl-sample-plate--uk">
                        <span className="sl-sample-plate-band" aria-hidden="true">
                          <img src="/gb-flag.svg" alt="" width="7" height="5" />
                          <span>GB</span>
                        </span>
                        <span className="sl-sample-plate-text">SA17 PLE</span>
                      </span>
                      <span className="text-xs font-medium text-muted-foreground">VIN: WDD2183651A123456</span>
                    </div>
                    <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                      Mercedes-Benz CLS 220 d AMG Line
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground sm:text-sm">
                      2017 · Coupé · Blue · Diesel · 2,143 cc · 7-Speed Automatic
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2 text-xs">
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-muted-foreground">
                        <Icon name="calendar" size={13} className="text-primary" />
                        First reg: 15 Mar 2017
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-muted-foreground">
                        <Icon name="shield-check" size={13} className="text-primary" />
                        Euro 6
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1 text-muted-foreground">
                        <Icon name="coins" size={13} className="text-primary" />
                        VED: £190 / yr
                      </span>
                    </div>
                  </div>

                  <div className="relative mt-4 flex w-full items-center justify-center overflow-visible md:mt-0">
                    <div className="pointer-events-none absolute h-36 w-36 rounded-full bg-primary/20 blur-2xl" />
                    <picture className="relative z-10 mx-auto block w-full max-w-[300px]">
                      <source srcSet="/service-lad-sky.webp" type="image/webp" />
                      <img
                        src="/service-lad-sky.png"
                        alt="Mercedes-Benz CLS 220 d illustration"
                        className="mx-auto h-auto max-h-52 w-full object-contain object-center drop-shadow-[0_16px_24px_rgba(59,130,246,0.25)] sm:max-h-40"
                      />
                    </picture>
                  </div>
                </div>
              </div>

              {/* Quick Status Cards Grid */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-xl border border-border bg-card p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-muted-foreground">Finance Check</span>
                    <Icon name="shield-check" size={16} className="text-emerald-500" />
                  </div>
                  <p className="mt-2 font-heading text-sm font-semibold text-emerald-600">None found</p>
                  <p className="mt-0.5 text-[10.5px] text-muted-foreground">No active lender agreements</p>
                </div>

                <div className="rounded-xl border border-border bg-card p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-muted-foreground">Stolen Check</span>
                    <Icon name="shield-check" size={16} className="text-emerald-500" />
                  </div>
                  <p className="mt-2 font-heading text-sm font-semibold text-emerald-600">Not recorded</p>
                  <p className="mt-0.5 text-[10.5px] text-muted-foreground">Police database clear</p>
                </div>

                <div className="sl-writeoff-alert-card rounded-xl p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="sl-writeoff-alert-label text-[11px] font-medium">Write-off Check</span>
                    <img src="/svgs/warning.svg" alt="" width={16} height={16} className="h-4 w-4" aria-hidden="true" />
                  </div>
                  <p className="sl-writeoff-alert-title mt-2 font-heading text-sm font-semibold">1 alert found</p>
                  <p className="sl-writeoff-alert-sub mt-0.5 text-[10.5px]">Category S recorded (2025)</p>
                </div>

                <div className="rounded-xl border border-primary/40 bg-primary/5 p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-primary">Service Record</span>
                    <Icon name="history" size={16} className="text-primary" />
                  </div>
                  <p className="mt-2 font-heading text-sm font-semibold text-primary">4 official logs</p>
                  <p className="mt-0.5 text-[10.5px] text-muted-foreground">Main dealer verified</p>
                </div>
              </div>

              {/* Notice note */}
              <div className="flex items-start gap-2.5 rounded-xl border border-border/80 bg-muted/30 p-3 text-xs text-muted-foreground">
                <Icon name="file-search" size={15} className="mt-0.5 shrink-0 text-primary" />
                <span>
                  Both <strong>{SL_RISK.name} (£{SL_RISK.plate})</strong> and <strong>{SL_COMPLETE.name} (£{SL_COMPLETE.plate})</strong> include stolen, finance, write-off and mileage checks. The service history version adds full manufacturer service logs.
                </span>
              </div>
            </div>
          ) : null}

          {/* PAGE 2: WRITE-OFF & DAMAGE (CAROAT-INSPIRED STAGE) */}
          {activePage === 1 ? (
            <div className="sl-report-page-anim space-y-5">
              {/* Section Sub-Header */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-orange-500/30 bg-orange-500/10 text-orange-600 dark:text-orange-400">
                    <Icon name="alert-triangle" size={16} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground">Write-off and damage history</h3>
                    <p className="text-xs text-muted-foreground">Insurance loss records and recorded damage locations.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isUkIncident(sampleDamageRecord) ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground">
                      <img src="/gb.png" alt="" className="h-3 w-auto rounded-[1px]" />
                      <span>United Kingdom</span>
                    </span>
                  ) : sampleDamageRecord.country ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-foreground">
                      <span>{sampleDamageRecord.country}</span>
                    </span>
                  ) : null}
                  <span className="rounded-full bg-orange-500/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-orange-700 dark:text-orange-400">
                    Attention
                  </span>
                </div>
              </div>

              {/* Main Damage Alert Banner */}
              <div className="flex items-start gap-4 rounded-2xl border border-orange-500/35 bg-orange-500/[0.07] p-4 sm:p-5">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-[0_8px_16px_-6px_rgba(234,88,12,0.5)]">
                  <Icon name="alert-triangle" size={22} />
                </div>
                <div className="min-w-0">
                  <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.2em] text-orange-700 dark:text-orange-400">
                    Damage Check
                  </p>
                  <h4 className="mt-0.5 font-heading text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                    Damage recorded on this vehicle
                  </h4>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                    Review the insurer record and highlighted areas before inspecting the car.
                  </p>
                </div>
              </div>

              {/* Damage Stage + Incident Details Stack */}
              <div className="grid gap-5 lg:grid-cols-[1.15fr_1fr]">
                {/* Visual Top-Down Damage Stage with Oval Highlight */}
                <div className="relative flex flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card/60 p-4 sm:p-5">
                  {/* Top-left Reported Tag */}
                  <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-500/30 bg-background px-3 py-1 text-xs font-semibold text-orange-700 dark:text-orange-400 shadow-sm">
                      <Icon name="alert-triangle" size={13} className="text-orange-600" />
                      Damage reported
                    </span>
                    <span className="text-[11px] text-muted-foreground">Top-down zone map</span>
                  </div>

                  {/* Oval Diagram Area with Real Top-Down Car Cutout Photo */}
                  <div className="relative my-2 flex h-64 w-full items-center justify-center">
                    {/* SVG Canvas for Pedestal, Base Ellipse Ring and Active Damage Arcs */}
                    <svg viewBox="0 0 320 240" className="absolute inset-0 h-full w-full select-none overflow-visible">
                      <defs>
                        <filter id="sl-damage-glow" x="-20%" y="-20%" width="140%" height="140%">
                          <feDropShadow dx="0" dy="0" stdDeviation="3" floodColor="#ea580c" floodOpacity="0.45" />
                        </filter>
                      </defs>

                      {/* Light studio pedestal under the car */}
                      <ellipse
                        cx="160"
                        cy="120"
                        rx="40"
                        ry="76"
                        className="fill-neutral-200"
                      />

                      {/* Base Grey Ellipse Ring (Vertical Oval matching car proportions) */}
                      <ellipse
                        cx="160"
                        cy="120"
                        rx="66"
                        ry="96"
                        fill="none"
                        className="stroke-border"
                        strokeWidth="3.5"
                      />

                      {/* One continuous arc when zones meet, so joins do not double-draw */}
                      {(() => {
                        const zones = sampleDamageRecord.damageZones;
                        const hasFront = zones.includes("front");
                        const hasNearside = zones.includes("nearside");
                        const hasOffside = zones.includes("offside");
                        const hasRear = zones.includes("rear");
                        const arcs = [];
                        if (hasFront && hasNearside) {
                          arcs.push("M 196 38 A 66 96 0 0 0 94 120");
                        } else {
                          if (hasFront) arcs.push("M 124 38 A 66 96 0 0 1 196 38");
                          if (hasNearside) arcs.push("M 160 24 A 66 96 0 0 0 94 120");
                        }
                        if (hasOffside) arcs.push("M 160 24 A 66 96 0 0 1 226 120");
                        if (hasRear) arcs.push("M 94 120 A 66 96 0 0 0 226 120");
                        return arcs.map((d) => (
                          <path
                            key={d}
                            d={d}
                            fill="none"
                            className="stroke-orange-500"
                            strokeWidth="5.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            filter="url(#sl-damage-glow)"
                          />
                        ));
                      })()}
                    </svg>

                    {/* Real Top-Down Car Image (Transparent PNG over the pedestal) */}
                    <img
                      src="/damage-car-top.png"
                      alt="Vehicle top-down view"
                      className="relative z-10 h-36 w-auto select-none object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.35)]"
                    />

                    {/* Zone Hotspot Interactive Pills (Positioned around vertical ring) */}
                    {["front", "nearside", "offside", "rear"].map((zone) => {
                      const isRecorded = sampleDamageRecord.damageZones.includes(zone);
                      const isSelected = selectedDamagePart === zone;
                      const label = zone.charAt(0).toUpperCase() + zone.slice(1);
                      const position =
                        zone === "front" ? "-top-0.5 left-1/2 -translate-x-1/2"
                          : zone === "nearside" ? "top-1/2 left-8 -translate-y-1/2"
                            : zone === "offside" ? "top-1/2 right-8 -translate-y-1/2"
                              : "-bottom-0.5 left-1/2 -translate-x-1/2";
                      return (
                        <button
                          key={zone}
                          type="button"
                          onClick={() => setSelectedDamagePart(zone)}
                          className={`absolute ${position} z-20 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold transition ${
                            isRecorded
                              ? (isSelected
                                ? "bg-orange-600 text-white shadow-md ring-2 ring-orange-300"
                                : "bg-orange-600/90 text-white shadow-sm hover:opacity-90")
                              : "border border-border/80 bg-background/90 font-medium text-muted-foreground opacity-60 hover:opacity-100"
                          }`}
                        >
                          {isRecorded ? (
                            <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-black/40 text-[9px] font-bold text-white">
                              !
                            </span>
                          ) : null}
                          {label}
                        </button>
                      );
                    })}

                    {/* Floating Bottom-Right Badge: Recorded Damage Area (Caroat Style) */}
                    <div className="absolute bottom-1 right-1 z-20 rounded-xl border border-border/80 bg-background/95 p-2 shadow-sm backdrop-blur-sm">
                      <span className="block text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">
                        Recorded damage area
                      </span>
                      <div className="mt-1 flex items-center gap-1">
                        {sampleDamageRecord.damageLocations.map((loc) => (
                          <span
                            key={loc}
                            className="rounded bg-orange-100 px-1.5 py-0.5 text-[10.5px] font-bold text-orange-700 dark:bg-orange-950/60 dark:text-orange-300"
                          >
                            {loc}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Incident Summary Card (Caroat Data Table Layout) */}
                <div className="flex flex-col justify-between space-y-3">
                  {/* Quick Verification Strip */}
                  <div className="space-y-2 rounded-2xl border border-border bg-card p-3.5 text-xs">
                    <div className="flex items-center justify-between border-b border-border/60 pb-2">
                      <span className="text-muted-foreground">{sampleDamageRecord.categoryName} marker recorded</span>
                      <span className="font-semibold text-orange-600 dark:text-orange-400">Yes</span>
                    </div>
                    <div className={`flex items-center justify-between${sampleDamageRecord.repairCostEstimate ? " border-b border-border/60 pb-2" : ""}`}>
                      <span className="text-muted-foreground">Damage marker found</span>
                      <span className="font-semibold text-orange-600 dark:text-orange-400">Yes</span>
                    </div>
                    {sampleDamageRecord.repairCostEstimate ? (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Repair cost estimate available</span>
                        <span className="font-semibold text-foreground">{sampleDamageRecord.repairCostEstimate}</span>
                      </div>
                    ) : null}
                  </div>

                  {/* Detailed Incident Facts */}
                  <div className="rounded-2xl border border-border bg-card p-4 text-xs">
                    <p className="font-heading text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Damage summary
                    </p>

                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {sampleDamageRecord.lossDate ? (
                        <div className="rounded-xl border border-border/80 bg-background p-2.5">
                          <span className="text-[10.5px] text-muted-foreground">Date of incident</span>
                          <p className="mt-0.5 font-semibold text-foreground">{sampleDamageRecord.lossDate}</p>
                        </div>
                      ) : null}

                      {sampleDamageRecord.insurerName ? (
                        <div className="rounded-xl border border-border/80 bg-background p-2.5">
                          <span className="text-[10.5px] text-muted-foreground">Insurer</span>
                          <p className="mt-0.5 font-semibold text-foreground">{sampleDamageRecord.insurerName}</p>
                        </div>
                      ) : null}

                      {sampleDamageRecord.damageType ? (
                        <div className="col-span-2 rounded-xl border border-border/80 bg-background p-2.5">
                          <span className="text-[10.5px] text-muted-foreground">Type of damage</span>
                          <p className="mt-0.5 font-semibold text-foreground">{sampleDamageRecord.damageType}</p>
                        </div>
                      ) : null}

                      {sampleDamageRecord.categoryLabel ? (
                        <div className="col-span-2 rounded-xl border border-orange-500/30 bg-orange-500/5 p-2.5">
                          <span className="text-[10.5px] text-orange-700 dark:text-orange-400">Insurer repair category</span>
                          <p className="mt-0.5 font-semibold text-foreground">
                            {sampleDamageRecord.categoryLabel}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Source Integrity Footer */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="check-circle-2" size={15} className="text-emerald-500" />
                    <span>Information sourced from insurance and industry databases.</span>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* PAGE 3: MANUFACTURER SERVICE HISTORY */}
          {activePage === 2 ? (
            <div className="sl-report-page-anim space-y-6">
              {/* Standout Header Banner */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                    <Icon name="history" size={18} />
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-bold text-foreground">
                      Manufacturer Digital Service Records
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Official Mercedes-Benz dealer network logs. Itemised work, dates, mileage and dealer locations.
                    </p>
                  </div>
                </div>
                <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                  Complete Report Exclusive
                </span>
              </div>

              {/* Service Timeline List */}
              <div className="space-y-3">
                <div className="relative rounded-xl border border-border bg-card p-4 transition hover:border-primary/50">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-primary">24,180 mi</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-medium text-foreground">
                        Major Service
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">12 Oct 2025 · Mercedes Official Service</span>
                  </div>
                  <p className="mt-2 text-xs text-foreground font-medium">Work carried out by authorised dealer:</p>
                  <ul className="mt-1.5 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                    <li className="flex items-center gap-1.5">
                      <Icon name="check" size={13} className="text-primary" /> Engine oil and microfilter replaced
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Icon name="check" size={13} className="text-primary" /> Spark plugs renewed
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Icon name="check" size={13} className="text-primary" /> Full 32-point electronic vehicle health check
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Icon name="check" size={13} className="text-primary" /> Brake fluid condition verified ok
                    </li>
                  </ul>
                </div>

                <div className="relative rounded-xl border border-border bg-card p-4 transition hover:border-primary/50">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-foreground">16,520 mi</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-medium text-foreground">
                        Brake Service
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">18 Mar 2025 · Mercedes Authorised Workshop</span>
                  </div>
                  <p className="mt-2 text-xs text-foreground font-medium">Work carried out:</p>
                  <ul className="mt-1.5 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                    <li className="flex items-center gap-1.5">
                      <Icon name="check" size={13} className="text-primary" /> Brake fluid flush & refill
                    </li>
                    <li className="flex items-center gap-1.5">
                      <Icon name="check" size={13} className="text-primary" /> Front & rear pads inspected (8mm remaining)
                    </li>
                  </ul>
                </div>

                <div className="relative rounded-xl border border-border bg-card p-4 transition hover:border-primary/50">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-heading font-bold text-foreground">8,310 mi</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-medium text-foreground">
                        First Inspection
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">04 Sep 2024 · Mercedes Centre</span>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Initial running-in oil service and diagnostic system check. Digital service book updated.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-border/80 bg-muted/20 p-3 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">No-Find-No-Fee Guarantee:</span> If no manufacturer service history records are found for an eligible car, the service history fee is released in full.
              </div>
            </div>
          ) : null}

          {/* PAGE 4: MILEAGE PROGRESSION & ROLLBACK */}
          {activePage === 3 ? (
            <div className="sl-report-page-anim space-y-6">
              {/* Header Status */}
              <div className="sl-mileage-verified-card flex items-center justify-between rounded-2xl border p-4">
                <div className="flex items-center gap-3">
                  <div className="sl-mileage-verified-icon flex h-9 w-9 shrink-0 items-center justify-center rounded-xl">
                    <Icon name="gauge" size={18} />
                  </div>
                  <div>
                    <h4 className="sl-mileage-verified-title font-heading text-sm font-bold">
                      Mileage Verified Across 4 Sources
                    </h4>
                    <p className="sl-mileage-verified-sub text-xs">
                      Continuous upward curve. No clocking or rollback anomalies detected.
                    </p>
                  </div>
                </div>
                <span className="sl-mileage-verified-badge rounded-full px-2.5 py-1 text-xs font-semibold">
                  Rollback Clean
                </span>
              </div>

              {/* Visual Mileage Curve Graph */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-baseline justify-between border-b border-border/60 pb-3">
                  <span className="text-xs font-semibold text-foreground">Mileage History Trendline</span>
                  <span className="text-xs text-muted-foreground">Avg. 7,850 mi / year</span>
                </div>

                <div className="py-4">
                  <svg viewBox="0 0 500 140" className="h-36 w-full overflow-visible">
                    {/* Grid Lines */}
                    <line x1="40" y1="20" x2="480" y2="20" stroke="currentColor" strokeOpacity="0.08" />
                    <line x1="40" y1="60" x2="480" y2="60" stroke="currentColor" strokeOpacity="0.08" />
                    <line x1="40" y1="100" x2="480" y2="100" stroke="currentColor" strokeOpacity="0.08" />

                    {/* Y Labels */}
                    <text x="30" y="24" textAnchor="end" className="fill-muted-foreground text-[10px]">30k</text>
                    <text x="30" y="64" textAnchor="end" className="fill-muted-foreground text-[10px]">20k</text>
                    <text x="30" y="104" textAnchor="end" className="fill-muted-foreground text-[10px]">10k</text>

                    {/* Smooth Mileage Area */}
                    <path
                      d="M 60 125 L 160 98 L 280 65 L 400 32 L 460 22 L 460 125 Z"
                      className="fill-primary/10"
                    />

                    {/* Smooth Mileage Curve */}
                    <path
                      d="M 60 125 L 160 98 L 280 65 L 400 32 L 460 22"
                      fill="none"
                      className="stroke-primary"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {/* Data Points */}
                    <circle cx="60" cy="125" r="4.5" className="fill-background stroke-primary stroke-[2.5]" />
                    <circle cx="160" cy="98" r="4.5" className="fill-background stroke-primary stroke-[2.5]" />
                    <circle cx="280" cy="65" r="4.5" className="fill-background stroke-primary stroke-[2.5]" />
                    <circle cx="400" cy="32" r="4.5" className="fill-background stroke-primary stroke-[2.5]" />
                    <circle cx="460" cy="22" r="5" className="fill-primary" />

                    {/* Milestone labels */}
                    <text x="60" y="138" textAnchor="middle" className="fill-muted-foreground text-[9.5px]">Delivery</text>
                    <text x="160" y="112" textAnchor="middle" className="fill-muted-foreground text-[9.5px]">8.3k</text>
                    <text x="280" y="79" textAnchor="middle" className="fill-muted-foreground text-[9.5px]">16.5k</text>
                    <text x="400" y="46" textAnchor="middle" className="fill-muted-foreground text-[9.5px]">24.2k</text>
                    <text x="460" y="12" textAnchor="middle" className="fill-primary font-bold text-[10px]">31.4k</text>
                  </svg>
                </div>

                <div className="grid grid-cols-2 gap-3 border-t border-border/60 pt-3 text-xs">
                  <div>
                    <span className="text-[10.5px] text-muted-foreground">Latest Recorded</span>
                    <p className="font-semibold text-foreground">31,420 miles</p>
                  </div>
                  <div>
                    <span className="text-[10.5px] text-muted-foreground">Odometer Integrity</span>
                    <p className="font-semibold text-emerald-600">Passed audit</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* PAGE 5: PLATES, V5C & IDENTITY */}
          {activePage === 4 ? (
            <div className="sl-report-page-anim space-y-6">
              {/* V5C Status Card */}
              <div className="rounded-2xl border border-border bg-card p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-3">
                  <div className="flex items-center gap-2">
                    <Icon name="bell-ring" size={16} className="text-primary" />
                    <h4 className="font-heading text-sm font-bold text-foreground">
                      V5C Document & Logbook Verification
                    </h4>
                  </div>
                  <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
                    DVLA Database
                  </span>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-3 text-xs">
                  <div className="rounded-xl border border-border bg-background p-3">
                    <span className="text-[10.5px] text-muted-foreground">Last V5C Issue Date</span>
                    <p className="mt-1 font-heading text-sm font-semibold text-foreground">24 Jan 2025</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">Matches plate transfer event</p>
                  </div>

                  <div className="rounded-xl border border-border bg-background p-3">
                    <span className="text-[10.5px] text-muted-foreground">Recorded Keepers</span>
                    <p className="mt-1 font-heading text-sm font-semibold text-foreground">1 previous keeper</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">Acquired 20 Sept 2024</p>
                  </div>

                  <div className="rounded-xl border border-border bg-background p-3">
                    <span className="text-[10.5px] text-muted-foreground">V5C Watch Option</span>
                    <p className="mt-1 font-heading text-sm font-semibold text-primary">£1.49 / 3 months</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">Alerts if logbook reissues</p>
                  </div>
                </div>
              </div>

              {/* Plate & Colour Changes */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
                    <span className="text-xs font-semibold text-foreground">Plate History</span>
                    <span className="text-[10.5px] text-muted-foreground">1 transfer</span>
                  </div>
                  <div className="mt-3 space-y-2 text-xs">
                    <div className="flex items-center justify-between rounded-lg border border-border bg-background p-2">
                      <span className="sl-sample-plate text-xs">AB12 CDE</span>
                      <span className="text-muted-foreground">Prior (Jan 2025 to Mar 2025)</span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border bg-background p-2">
                      <span className="sl-sample-plate is-old text-xs">SL80 XXX</span>
                      <span className="text-muted-foreground">Earlier (Mar 2017 to Jan 2025)</span>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-center justify-between border-b border-border/60 pb-2.5">
                    <span className="text-xs font-semibold text-foreground">Colour & Export Trail</span>
                    <span className="text-[10.5px] text-emerald-600 font-medium">Clear</span>
                  </div>
                  <div className="mt-3 space-y-2 text-xs">
                    <div className="flex items-center justify-between rounded-lg border border-border bg-background p-2.5">
                      <span className="text-muted-foreground">Current Colour</span>
                      <span className="font-semibold text-foreground">Blue (Original Factory)</span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border bg-background p-2.5">
                      <span className="text-muted-foreground">Colour Changes</span>
                      <span className="font-semibold text-foreground">None recorded</span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border bg-background p-2.5">
                      <span className="text-muted-foreground">Scrapped / Exported</span>
                      <span className="font-semibold text-emerald-600">No scrap or export record</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Bottom Card Footer: Bundle comparison & Quick Jump CTA */}
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-border/70 pt-5">
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Bundle Summary:</span>
              <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5">
                <Icon name="check" size={12} className="text-primary" />
                {SL_RISK.name} £{SL_RISK.plate} (Pages 1, 2, 4, 5)
              </span>
              <span className="inline-flex items-center gap-1 rounded-md bg-primary/15 px-2 py-0.5 font-medium text-primary">
                <Icon name="check" size={12} className="text-primary" />
                {SL_COMPLETE.name} £{SL_COMPLETE.plate} (All Pages + Service Logs)
              </span>
            </div>

            <button
              type="button"
              onClick={goToLookup}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            >
              Check your vehicle registration <Icon name="arrow-right" size={14} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
